import { useEffect, useMemo, useState } from 'react';
import {
  classifyPlanAction,
  createCentralBillingClient,
  describeBillingError,
  filterPlansForWorkspace,
  formatCents,
  formatPlanPrice,
  type CentralPlan,
  type PendingPlanChange,
  type PlanPreview,
} from '@bb-dev123/central-billing';
import { centralFetcher } from '../../../utils/centralFetcher';
import { buildBillingReturnUrl } from '../../../utils/billingReturn';

/**
 * Plan picker — the `?view=plans` mode of the Billing settings tab.
 *
 * ONE implementation with the apps: the plan rules (which plans this
 * workspace sees, "Contact us" never "$0", current/scheduled markers, change
 * vs checkout, owner/admin only) and the error wording come from
 * @bb-dev123/central-billing (`filterPlansForWorkspace`, `formatPlanPrice`,
 * `classifyPlanAction`, `describeBillingError`), and every call goes through
 * the shared typed client (`createCentralBillingClient`) — the same code the
 * suite apps' in-app Settings use.
 *
 * Flow: choose → POST /api/billing/plan/preview → review (amounts only when
 * Central marks them reliable) → confirm:
 *   - live provider subscription → POST /api/billing/plan/change with the
 *     preview token (409 PREVIEW_STALE when the price moved)
 *   - none → POST /api/billing/plan/checkout → Stripe Checkout, returning to
 *     /billing/return (utils/billingReturn — supports Central paths)
 * A scheduled change shows with "Keep my current plan"
 * (POST /api/billing/plan/change/cancel).
 */

/** Exact mounted paths (pinned by billingRoutesMounted.test.ts / BillingTab tests). */
export const PLAN_CATALOG_URL = '/api/subscriptions/plans';
export const PLAN_CHECKOUT_URL = '/api/billing/plan/checkout';
export const PLAN_CHANGE_URL = '/api/billing/plan/change';
export const PLAN_PREVIEW_URL = '/api/billing/plan/preview';
export const PLAN_CHANGE_CANCEL_URL = '/api/billing/plan/change/cancel';

export type CatalogPlan = CentralPlan;

export interface PlanPickerProps {
  workspaceId: string;
  authToken: string;
  workspaceType: string | null;
  currentPlanCode: string | null;
  canManage: boolean;
  /** A provider subscription exists and is not canceled → use plan change. */
  hasLiveSubscription: boolean;
  /** Seats currently in use, passed to checkout so the price matches the summary. */
  seatCount: number | null;
  /** Validated-later destination after checkout (app URL or Central path). */
  returnTo: string | null;
  /** Scheduled change from /api/subscription/me (`pendingChange`). */
  pendingChange?: PendingPlanChange | null;
  onBack: () => void;
  onChanged: (message: string) => void;
}

function formatDay(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

export function PlanPicker({
  workspaceId,
  authToken,
  workspaceType,
  currentPlanCode,
  canManage,
  hasLiveSubscription,
  seatCount,
  returnTo,
  pendingChange = null,
  onBack,
  onChanged,
}: PlanPickerProps) {
  const client = useMemo(() => createCentralBillingClient(centralFetcher(authToken)), [authToken]);
  const [plans, setPlans] = useState<CentralPlan[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [busyPlan, setBusyPlan] = useState<string | null>(null);
  const [actionError, setActionError] = useState('');
  const [review, setReview] = useState<{ plan: CentralPlan; preview: PlanPreview } | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [keepingCurrent, setKeepingCurrent] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await client.listPlans();
      if (cancelled) return;
      if (res.ok) setPlans(res.data);
      else setLoadError(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [client]);

  const visiblePlans = filterPlansForWorkspace(plans ?? [], workspaceType);
  const currentPlan = (plans ?? []).find((p) => p.code === currentPlanCode) ?? null;
  const currentPrice = currentPlan && currentPlan.priceAvailable !== false ? currentPlan.monthlyPriceCents : null;

  async function choose(plan: CentralPlan) {
    setBusyPlan(plan.code);
    setActionError('');
    const res = await client.previewPlanChange({ workspaceId, planCode: plan.code });
    setBusyPlan(null);
    if (!res.ok) {
      setActionError(describeBillingError(res));
      return;
    }
    setReview({ plan, preview: res.data });
  }

  async function confirm() {
    if (!review) return;
    const { plan, preview } = review;
    setConfirming(true);
    setActionError('');
    try {
      if (preview.action === 'change') {
        const res = await client.changePlan({ workspaceId, planCode: plan.code, previewToken: preview.previewToken });
        if (!res.ok) {
          setActionError(describeBillingError(res));
          return;
        }
        if (returnTo) {
          window.location.assign(
            buildBillingReturnUrl({
              origin: window.location.origin,
              status: 'success',
              kind: 'plan_change',
              workspaceId,
              plan: res.data.effective === 'immediate' ? plan.code : null,
              returnTo,
            }),
          );
          return;
        }
        onChanged(
          res.data.effective === 'next_cycle'
            ? `Your plan will change to ${plan.name}${
                res.data.pendingChange?.effectiveAt ? ` on ${formatDay(res.data.pendingChange.effectiveAt)}` : ' at the end of the current billing period'
              }.`
            : `Your plan is now ${plan.name}.`,
        );
        return;
      }

      const destination = returnTo || `/workspace/${workspaceId}/settings/billing`;
      const res = await client.startCheckout({
        workspaceId,
        planCode: plan.code,
        ...(seatCount && seatCount > 0 ? { seatCount } : {}),
        successUrl: buildBillingReturnUrl({
          origin: window.location.origin,
          status: 'success',
          kind: 'plan',
          workspaceId,
          plan: plan.code,
          returnTo: destination,
          withStripeSessionId: true,
        }),
        cancelUrl: buildBillingReturnUrl({
          origin: window.location.origin,
          status: 'cancel',
          kind: 'plan',
          workspaceId,
          returnTo: destination,
        }),
      });
      if (res.ok && typeof res.data.url === 'string') {
        window.location.assign(res.data.url);
        return;
      }
      setActionError(res.ok ? describeBillingError(null) : describeBillingError(res));
    } finally {
      setConfirming(false);
    }
  }

  async function keepCurrentPlan() {
    setKeepingCurrent(true);
    setActionError('');
    const res = await client.cancelPendingChange({ workspaceId });
    setKeepingCurrent(false);
    if (!res.ok) {
      setActionError(describeBillingError(res));
      return;
    }
    onChanged('Your scheduled change was canceled. You stay on your current plan.');
  }

  function action(plan: CentralPlan) {
    const a = classifyPlanAction({
      plan,
      currentPlanCode,
      currentPriceCents: currentPrice,
      canManage,
      hasLiveSubscription,
      pendingChange,
    });
    if (a.kind === 'current') return <span className="text-sm font-medium text-green-700">Current plan</span>;
    if (a.kind === 'pending') return <span className="text-sm font-medium text-blue-700">Scheduled</span>;
    if (a.kind === 'none') return null;
    if (a.kind === 'contact') return <span className="text-sm text-gray-500">{a.label}</span>;
    return (
      <button
        type="button"
        onClick={() => choose(plan)}
        disabled={busyPlan !== null || review !== null}
        className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50"
      >
        {busyPlan === plan.code ? 'Processing...' : a.label}
      </button>
    );
  }

  const reviewRows = review?.preview.reliable
    ? [
        ['Due today', formatCents(review.preview.amountDueNowCents, review.preview.currency)],
        ['Prorated adjustment on next invoice', review.preview.prorationCents ? formatCents(review.preview.prorationCents, review.preview.currency) : null],
        ['Next invoice', formatCents(review.preview.nextInvoiceCents, review.preview.currency)],
      ].filter((r): r is [string, string] => r[1] !== null)
    : [];

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-900/5 p-6">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium text-gray-900">Choose a plan</h2>
          <button type="button" onClick={onBack} className="text-sm text-emerald-600 hover:text-emerald-700">
            Back to billing
          </button>
        </div>
        {!canManage && (
          <p className="text-sm text-gray-600 mt-3">
            Only workspace owners and admins can change the plan. Ask your workspace owner.
          </p>
        )}
        {pendingChange && (
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-lg bg-blue-50 border border-blue-200 p-3" data-testid="plan-pending">
            <p className="text-sm text-blue-900">
              {pendingChange.kind === 'cancel_to_free'
                ? `Your paid plan ends${pendingChange.effectiveAt ? ` on ${formatDay(pendingChange.effectiveAt)}` : ' at the end of this billing period'}; you then move to ${pendingChange.planName ?? pendingChange.planCode}.`
                : `Changing to ${pendingChange.planName ?? pendingChange.planCode}${pendingChange.effectiveAt ? ` on ${formatDay(pendingChange.effectiveAt)}` : ' at the end of this billing period'}.`}
            </p>
            {canManage && (
              <button
                type="button"
                onClick={keepCurrentPlan}
                disabled={keepingCurrent}
                className="px-3 py-1.5 text-sm font-medium text-blue-700 border border-blue-300 rounded-lg hover:bg-blue-100 disabled:opacity-50"
                data-testid="plan-keep-current"
              >
                {keepingCurrent ? 'Working...' : 'Keep my current plan'}
              </button>
            )}
          </div>
        )}
        {actionError && (
          <p role="alert" className="text-sm text-red-600 mt-3">
            {actionError}
          </p>
        )}
      </div>

      {review && (
        <div
          className="bg-white rounded-2xl shadow-sm ring-1 ring-emerald-600/30 p-6"
          role="dialog"
          aria-labelledby="plan-review-title"
          data-testid="plan-review"
        >
          <h3 id="plan-review-title" className="text-base font-semibold text-gray-900">
            {review.preview.currentPlan.name ? `${review.preview.currentPlan.name} → ${review.plan.name}` : review.plan.name}
          </h3>
          <p className="text-sm text-gray-600 mt-2" data-testid="plan-review-effect">
            {review.preview.effect === 'checkout'
              ? review.preview.trialDays
                ? `Start your ${review.preview.trialDays}-day trial of ${review.plan.name}. You'll confirm payment details on a secure checkout page.`
                : `Continue to a secure checkout page to subscribe to ${review.plan.name}.`
              : review.preview.effect === 'immediate'
                ? `${review.plan.name} starts now. The prorated difference for the rest of this billing period is added to your next invoice.`
                : `You keep your current plan until ${formatDay(review.preview.effectiveAt) ?? 'the end of this billing period'}. ${review.plan.name} starts at your next renewal — no refund, no charge today.`}
          </p>
          {reviewRows.length > 0 ? (
            <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-sm" data-testid="plan-review-amounts">
              {reviewRows.map(([label, value]) => (
                <div key={label} className="contents">
                  <dt className="text-gray-500">{label}</dt>
                  <dd className="text-right font-medium text-gray-900">{value}</dd>
                </div>
              ))}
            </dl>
          ) : (
            <p className="text-xs text-gray-500 mt-3" data-testid="plan-review-no-quote">
              {review.preview.effect === 'checkout'
                ? 'The exact amount, including any tax, is shown on the secure checkout page before you pay.'
                : 'The exact amount will appear on your next invoice.'}
            </p>
          )}
          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setReview(null);
                setActionError('');
              }}
              disabled={confirming}
              className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              data-testid="plan-review-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={confirm}
              disabled={confirming}
              className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50"
              data-testid="plan-review-confirm"
            >
              {confirming
                ? 'Processing...'
                : review.preview.effect === 'checkout'
                  ? 'Continue to checkout'
                  : review.preview.effect === 'immediate'
                    ? 'Confirm upgrade'
                    : 'Schedule change'}
            </button>
          </div>
        </div>
      )}

      {plans === null && !loadError && (
        <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-900/5 p-8 text-center">
          <div className="animate-spin w-6 h-6 border-2 border-emerald-600 border-t-transparent rounded-full mx-auto"></div>
        </div>
      )}

      {(loadError || (plans !== null && visiblePlans.length === 0)) && (
        <div className="bg-gray-50 rounded-lg border border-gray-200 p-6 text-center">
          <p className="text-gray-600 font-medium">Plans are unavailable right now</p>
          <p className="text-sm text-gray-500 mt-1">Please try again later or contact support.</p>
        </div>
      )}

      {visiblePlans.length > 0 && (
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-4" aria-label="Available plans">
          {visiblePlans.map((plan) => (
            <li
              key={plan.code}
              data-testid={`plan-${plan.code}`}
              className={`rounded-lg border-2 p-5 bg-white ${
                plan.code === currentPlanCode ? 'border-green-500' : 'border-gray-200'
              }`}
            >
              <div className="flex items-baseline justify-between">
                <h3 className="text-base font-semibold text-gray-900">{plan.name}</h3>
                <span className="text-lg font-semibold text-gray-900" data-testid={`plan-price-${plan.code}`}>
                  {formatPlanPrice(plan)}
                </span>
              </div>
              {plan.description && <p className="text-sm text-gray-500 mt-1">{plan.description}</p>}
              <ul className="text-sm text-gray-600 mt-3 space-y-1">
                {typeof plan.baseCredits === 'number' && <li>{plan.baseCredits.toLocaleString()} credits / month</li>}
                {typeof plan.includedSeats === 'number' && <li>{plan.includedSeats} seats included</li>}
                {(plan.outcomes ?? []).length > 0 && (
                  <li>Outcomes: {(plan.outcomes ?? []).map((o) => o.displayName).join(', ')}</li>
                )}
                {(plan.appsUnlocked ?? []).length > 0 && (
                  <li>Apps: {(plan.appsUnlocked ?? []).map((a) => a.displayName).join(', ')}</li>
                )}
              </ul>
              <div className="mt-4">{action(plan)}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
